import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import { handleUIEvent } from "./eventLogger.js";
import { useDateFilter } from "./_40b_date_filter_context.js";

function Tooltip({ text }) {
    return (
        <span
            style={{
                marginLeft: 4,
                verticalAlign: "middle",
                cursor: "pointer",
                color: "#888",
                fontSize: 18
            }}
            title={text}
            onClick={e => handleUIEvent(e, { tooltip_text: text }, null, "CalendarSelector", "_42_calendar_selector.js")}
        >
            🛈
        </span>
    );
}

const WEEKDAYS = ["S", "M", "T", "W", "T", "F", "S"];
const MONTH_NAMES = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
];
const MONTH_ABBREV = [
    "Jan", "Feb", "Mar", "Apr", "May", "Jun",
    "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
];

const quarterMonths = {
    q1: [1, 2, 3],
    q2: [4, 5, 6],
    q3: [7, 8, 9],
    q4: [10, 11, 12]
};

function getMonthAbbrev(monthStr) {
    const [year, month] = monthStr.split("-");
    return MONTH_ABBREV[parseInt(month, 10) - 1] || monthStr;
}
function getMonthFull(monthStr) {
    const [year, month] = monthStr.split("-");
    return MONTH_NAMES[parseInt(month, 10) - 1] || monthStr;
}
function getWeekday(dateStr) {
    const [year, month, day] = dateStr.split("-");
    const d = new Date(Number(year), Number(month) - 1, Number(day));
    return d.getDay();
}
function chunkArray(arr, chunkSize) {
    const chunks = [];
    for (let i = 0; i < arr.length; i += chunkSize) {
        chunks.push(arr.slice(i, i + chunkSize));
    }
    return chunks;
}

function getYearFromMonth(monthStr) {
    return monthStr.split("-")[0];
}

function getMonthNum(monthStr) {
    return parseInt(monthStr.split("-")[1], 10);
}

function getQuarterFromMonth(monthNum) {
    if (monthNum <= 3) return "q1";
    if (monthNum <= 6) return "q2";
    if (monthNum <= 9) return "q3";
    return "q4";
}

function getDayFromDate(dateStr) {
    const [year, month, day] = dateStr.split("-");
    return parseInt(day, 10);
}

function formatSelectedDayUS(dateStr) {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    const weekday = d.toLocaleString("en-US", { weekday: "short" });
    const month = d.toLocaleString("en-US", { month: "short" });
    const day = d.getDate().toString().padStart(2, "0");
    return `${weekday}, ${month} ${day}`;
}

export default function CalendarSelector({
    activeDate,
    onDateChange
}) {
    const { navigationDate, setNavigationDate } = useDateFilter();
    const [months, setMonths] = useState([]);
    const [latestMonth, setLatestMonth] = useState("");
    const [selectedMonth, setSelectedMonth] = useState("");
    const [days, setDays] = useState([]);
    const [selectedDay, setSelectedDay] = useState("");
    const [tooltip, setTooltip] = useState("");
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [selectStatus, setSelectStatus] = useState("");

    // View state: "month", "year", "years"
    const [view, setView] = useState("month");
    const [selectedYear, setSelectedYear] = useState("");
    const [selectedQuarter, setSelectedQuarter] = useState("q1");
    const [collapsed, setCollapsed] = useState(true);
    const [hoveredDate, setHoveredDate] = useState("");

    const calendarRef = useRef(null);
    const headlineRef = useRef(null);
    const isInitialMount = useRef(true);

    // Collapse on outside click
    useEffect(() => {
        if (!collapsed) {
            const handleClickOutside = (event) => {
                if (
                    calendarRef.current &&
                    !calendarRef.current.contains(event.target) &&
                    (!headlineRef.current || !headlineRef.current.contains(event.target))
                ) {
                    setCollapsed(true);
                    handleUIEvent(event, { action: "calendar_collapsed_outside_click" }, null, "CalendarSelector", "_42_calendar_selector.js");
                }
            };
            document.addEventListener("mousedown", handleClickOutside);
            return () => {
                document.removeEventListener("mousedown", handleClickOutside);
            };
        }
    }, [collapsed]);

    // Get all years present in months data
    const allYears = Array.from(new Set(months.map(m => getYearFromMonth(m.month)))).sort();

    useEffect(() => {
        // Prevent double-firing or premature firing during initial mount 
        // until navigationDate/activeDate are actually ready.
        if (isInitialMount.current) {
            isInitialMount.current = false;
            // Only skip if we don't have a date to show yet
            if (!navigationDate && !activeDate) return;
        }

        setLoading(true);
        axios.get(`${process.env.REACT_APP_API_URL}/api/calendar-selector`, {
            withCredentials: true
        })
            .then(res => {
                setMonths(res.data.months || []);
                setLatestMonth(res.data.latest_month || "");
                setTooltip(res.data.tooltip || "");
                
                const effectiveDate = navigationDate || activeDate || res.data.latest_month || "";
                setSelectedMonth(effectiveDate.slice(0, 7));
                setDays(res.data.days || []);
                
                if (effectiveDate && res.data.days) {
                    setSelectedDay(effectiveDate);
                    setHoveredDate(effectiveDate);
                } else {
                    const todayObj = (res.data.days || []).find(d => d.is_today);
                    setSelectedDay(todayObj ? todayObj.date : "");
                    setHoveredDate(todayObj ? todayObj.date : "");
                }
                
                const year = getYearFromMonth(effectiveDate.slice(0, 7));
                const monthNum = getMonthNum(effectiveDate.slice(0, 7));
                setSelectedYear(year);
                setSelectedQuarter(getQuarterFromMonth(monthNum));
                setLoading(false);
            })
            .catch(err => {
                setError("Failed to load calendar data.");
                handleUIEvent(null, { error: err.message, action: "calendar_api_error" }, null, "CalendarSelector", "_42_calendar_selector.js");
                setLoading(false);
            });
    }, [navigationDate, activeDate]);

    function handleMonthSelect(month, e) {
        setSelectedMonth(month);
        setSelectedYear(getYearFromMonth(month));
        setSelectedQuarter(getQuarterFromMonth(getMonthNum(month)));
        setSelectStatus("Loading days...");
        handleUIEvent(e, { action: "calendar_month_select", month }, null, "CalendarSelector", "_42_calendar_selector.js");
        axios.get(`${process.env.REACT_APP_API_URL}/api/calendar-selector`, {
            params: { month },
            withCredentials: true
        })
            .then(res => {
                setDays(res.data.days || []);
                setSelectStatus("");
                setView("month");
                const todayObj = (res.data.days || []).find(d => d.is_today);
                if (todayObj) {
                    setSelectedDay(todayObj.date);
                    setHoveredDate(todayObj.date);
                } else {
                    const firstEnabled = (res.data.days || []).find(d => !d.disabled);
                    setSelectedDay(firstEnabled ? firstEnabled.date : "");
                    setHoveredDate(firstEnabled ? firstEnabled.date : "");
                }
            })
            .catch(err => {
                setSelectStatus("Failed to load days.");
                handleUIEvent(null, { error: err.message, action: "calendar_month_api_error" }, null, "CalendarSelector", "_42_calendar_selector.js");
            });
    }

    function handleDaySelect(dayObj, e) {
        if (dayObj.disabled) return;
        setSelectedDay(dayObj.date);
        setHoveredDate(dayObj.date);
        setSelectStatus("Saving...");
        handleUIEvent(e, { action: "calendar_day_select", date: dayObj.date }, null, "CalendarSelector", "_42_calendar_selector.js");
        
        setNavigationDate(dayObj.date);
        
        axios.post(`${process.env.REACT_APP_API_URL}/api/calendar-selector/select`, {
            selected_date: dayObj.date
        }, {
            withCredentials: true
        })
            .then(res => {
                setSelectStatus("Date selected!");
                if (onDateChange) onDateChange(dayObj.date);
                setCollapsed(true);
            })
            .catch(err => {
                setSelectStatus("Failed to select date.");
                handleUIEvent(null, { error: err.message, action: "calendar_day_api_error" }, null, "CalendarSelector", "_42_calendar_selector.js");
            });
    }

    function handleQuarterSelect(quarter, e) {
        setSelectedQuarter(quarter);
        const firstMonth = `${selectedYear}-${String(quarterMonths[quarter][0]).padStart(2, "0")}`;
        handleMonthSelect(firstMonth, e);
        handleUIEvent(e, { action: "calendar_quarter_select", quarter, year: selectedYear }, null, "CalendarSelector", "_42_calendar_selector.js");
    }

    function renderCalendarGrid() {
        if (!days.length) return null;
        const firstDayIdx = getWeekday(days[0].date);
        const grid = [];
        let week = new Array(7).fill(null);
        let dayIdx = 0;

        for (let i = 0; i < firstDayIdx; i++) {
            week[i] = null;
        }

        for (let i = firstDayIdx; i < 7 && dayIdx < days.length; i++) {
            week[i] = days[dayIdx++];
        }
        grid.push(week);

        while (dayIdx < days.length) {
            week = new Array(7).fill(null);
            for (let i = 0; i < 7 && dayIdx < days.length; i++) {
                week[i] = days[dayIdx++];
            }
            grid.push(week);
        }

        return (
            <table style={{ width: "100%", borderCollapse: "collapse", marginTop: 12 }}>
                <thead>
                    <tr>
                        {WEEKDAYS.map((wd, idx) => (
                            <th key={idx} style={{
                                padding: "6px 0",
                                fontWeight: 600,
                                fontSize: 15,
                                color: "#888",
                                textAlign: "center"
                            }}>{wd}</th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {grid.map((week, wIdx) => (
                        <tr key={wIdx}>
                            {week.map((dayObj, dIdx) => (
                                <td key={dIdx} style={{ textAlign: "center", padding: 0 }}>
                                    {dayObj ? (
                                        <button
                                            onClick={e => handleDaySelect(dayObj, e)}
                                            onMouseEnter={() => setHoveredDate(dayObj.date)}
                                            disabled={dayObj.disabled}
                                            style={{
                                                background: dayObj.has_trade ? "#fff" : "#eee",
                                                color: dayObj.disabled ? "#aaa" : "#222",
                                                border: dayObj.date === selectedDay
                                                    ? "2px solid #00FFFF"
                                                    : "1px solid #ccc",
                                                borderRadius: 6,
                                                padding: "8px 0",
                                                width: 36,
                                                height: 36,
                                                fontWeight: 500,
                                                fontSize: 15,
                                                cursor: dayObj.disabled ? "not-allowed" : "pointer",
                                                boxShadow: dayObj.date === selectedDay ? "0 2px 8px #00FFFF" : "none",
                                                margin: 2,
                                                transition: "border 0.2s"
                                            }}
                                            title={dayObj.has_trade ? "Trade exists" : "No trades"}
                                        >
                                            {parseInt(dayObj.date.slice(-2), 10)}
                                        </button>
                                    ) : null}
                                </td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
        );
    }                           

    function renderQuarterSelector() {
        const quarters = ["q1", "q2", "q3", "q4"];
        const quarterLabels = { q1: "Q1", q2: "Q2", q3: "Q3", q4: "Q4" };

        return (
            <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 12 }}>
                {/* Quarter buttons */}
                <div style={{ display: "flex", gap: 4 }}>
                    {quarters.map(quarter => {
                        const isSelected = selectedQuarter === quarter;
                        return (
                            <button
                                key={quarter}
                                style={{
                                    padding: "6px 12px",
                                    borderRadius: 4,
                                    border: isSelected ? "2px solid #00FFFF" : "1px solid #555",
                                    background: isSelected ? "rgba(0, 255, 255, 0.1)" : "#1e1f2e",
                                    color: isSelected ? "#00FFFF" : "#888",
                                    fontWeight: isSelected ? "bold" : "normal",
                                    cursor: "pointer",
                                    transition: "all 0.2s ease",
                                    fontSize: "12px",
                                    flex: 1,
                                    textAlign: "center"
                                }}
                                onClick={e => handleQuarterSelect(quarter, e)}
                                title={`View Q${quarter.slice(1)} (${quarterLabels[quarter]})`}
                            >
                                {quarterLabels[quarter]}
                            </button>
                        );
                    })}
                </div>

                {/* Months within selected quarter */}
                <div style={{ display: "flex", gap: 4 }}>
                    {quarterMonths[selectedQuarter].map(m => {
                        const monthStr = `${selectedYear}-${String(m).padStart(2, "0")}`;
                        const monthData = months.find(mon => mon.month === monthStr);
                        const isSelected = getMonthNum(selectedMonth) === m;

                        return (
                            <button
                                key={m}
                                style={{
                                    padding: "4px 10px",
                                    borderRadius: 3,
                                    border: isSelected ? "2px solid #00FFFF" : "1px solid #444",
                                    background: isSelected ? "rgba(0, 255, 255, 0.15)" : "#1e1f2e",
                                    color: isSelected ? "#00FFFF" : "#888",
                                    fontWeight: isSelected ? "600" : "normal",
                                    cursor: "pointer",
                                    transition: "all 0.2s ease",
                                    fontSize: "11px",
                                    flex: 1,
                                    textAlign: "center"
                                }}
                                onClick={e => handleMonthSelect(monthStr, e)}
                                title={`View ${MONTH_NAMES[m - 1]} ${selectedYear}`}
                            >
                                {MONTH_ABBREV[m - 1]}
                            </button>
                        );
                    })}
                </div>
            </div>
        );
    }

    function renderYearGrid(year) {
        const monthsForYear = months.filter(m => getYearFromMonth(m.month) === year);
        monthsForYear.sort((a, b) => getMonthNum(a.month) - getMonthNum(b.month));
        const rows = chunkArray(monthsForYear, 4);
        return (
            <div style={{ marginTop: 16 }}>
                {rows.map((row, rowIdx) => (
                    <div key={rowIdx} style={{ display: "flex", gap: 12, marginBottom: 8, justifyContent: "center" }}>
                        {row.map(m => (
                            <button
                                key={m.month}
                                onClick={e => handleMonthSelect(m.month, e)}
                                style={{
                                    background: "#1e1f2e",
                                    color: "#CCC",
                                    border: m.month === selectedMonth
                                        ? "2px solid #00FFFF"
                                        : "1px solid #444",
                                    borderRadius: 8,
                                    padding: "18px 0",
                                    fontWeight: 700,
                                    fontSize: 18,
                                    cursor: "pointer",
                                    boxShadow: m.month === selectedMonth ? "0 2px 8px rgba(0,255,255,0.3)" : "none",
                                    transition: "border 0.2s",
                                    minWidth: 60,
                                    width: 60,
                                    height: 60,
                                    textAlign: "center",
                                    backgroundClip: "padding-box"
                                }}
                                title={`View ${getMonthFull(m.month)}`}
                            >
                                {getMonthAbbrev(m.month)}
                            </button>
                        ))}
                    </div>
                ))}
            </div>
        );
    }

    function renderYearsGrid() {
        const years = allYears.slice(-3);
        return (
            <div style={{ marginTop: 16, display: "flex", flexDirection: "column", gap: 8, alignItems: "center" }}>
                {years.map(year => (
                    <button
                        key={year}
                        onClick={e => {
                            setSelectedYear(year);
                            setView("year");
                            handleUIEvent(e, { action: "calendar_year_select", year }, null, "CalendarSelector", "_42_calendar_selector.js");
                        }}
                        style={{
                            background: "#1e1f2e",
                            color: "#CCC",
                            border: year === selectedYear
                                ? "2px solid #00FFFF"
                                : "1px solid #444",
                            borderRadius: 10,
                            padding: "18px 0",
                            fontWeight: 700,
                            fontSize: 22,
                            cursor: "pointer",
                            boxShadow: year === selectedYear ? "0 2px 8px rgba(0,255,255,0.3)" : "none",
                            minWidth: 80,
                            textAlign: "center",
                            marginBottom: 4,
                            transition: "border 0.2s"
                        }}
                    >
                        {year}
                    </button>
                ))}
            </div>
        );
    }

    function formatDateInfo() {
        const dateToUse = hoveredDate || selectedDay || "";
        if (!dateToUse) return { month: "", day: "", year: "" };
        const [year, month, day] = dateToUse.split("-");
        return {
            month: MONTH_ABBREV[parseInt(month, 10) - 1],
            day: parseInt(day, 10),
            year: year
        };
    }

    if (collapsed) {
        return (
            <div style={{
                display: "flex",
                flexDirection: "row",
                alignItems: "flex-end",
                justifyContent: "center",
                background: "#181A25",
                borderRadius: 12,
                cursor: "pointer",
                margin: 0,
                padding: "4px 8px",
                minWidth: 0,
                width: "auto",
                height: "auto"
            }}
                onClick={e => {
                    setCollapsed(false);
                    handleUIEvent(e, { action: "calendar_expanded" }, null, "CalendarSelector", "_42_calendar_selector.js");
                }}
                title="Open calendar"
            >
                <span style={{ fontSize: 24, color: "#00FFFF" }}>📅</span>
            </div>
        );
    }

    const dateInfo = formatDateInfo();
    const monthData = months.find(m => m.month === selectedMonth);

    return (
        <>
            {/* Overlay */}
            <div
                style={{
                    position: "fixed",
                    top: 0,
                    left: 0,
                    width: "100vw",
                    height: "100vh",
                    background: "rgba(24,26,37,0.5)",
                    backdropFilter: "blur(6px)",
                    zIndex: 10000, // Increased to ensure highest z-order
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center"
                }}
            />
            <div
                ref={calendarRef}
                style={{
                    display: "flex",
                    flexDirection: "row",
                    background: "#181A25",
                    borderRadius: 12,
                    boxShadow: "0 2px 8px rgba(0,255,255,0.08)",
                    margin: 0,
                    position: "fixed",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    zIndex: 10001, // Increased to ensure popup is above all overlays
                    overflow: "hidden"
                }}
            >
                {/* Left Panel */}
                <div style={{
                    background: "linear-gradient(135deg, #1e5a96 0%, #0f4c81 100%)",
                    padding: "24px 20px",
                    minWidth: 140,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#fff",
                    position: "relative"
                }}>
                    <button
                        onClick={e => {
                            setCollapsed(true);
                            handleUIEvent(e, { action: "calendar_collapsed" }, null, "CalendarSelector", "_42_calendar_selector.js");
                        }}
                        style={{
                            position: "absolute",
                            top: 4,
                            right: 4,
                            background: "none",
                            border: "none",
                            color: "#00FFFF",
                            fontSize: 10,
                            cursor: "pointer"
                        }}
                        title="Collapse calendar"
                    >✕</button>

                    <div style={{
                        fontSize: 24,
                        fontWeight: 700,
                        letterSpacing: 2,
                        marginBottom: 12,
                        textTransform: "uppercase"
                    }}>
                        {dateInfo.month}
                    </div>

                    <div style={{
                        fontSize: 64,
                        fontWeight: 700,
                        lineHeight: 1,
                        marginBottom: 12
                    }}>
                        {dateInfo.day}
                    </div>

                    <div style={{
                        fontSize: 24,
                        fontWeight: 600
                    }}>
                        {dateInfo.year}
                    </div>
                </div>

                {/* Right Panel */}
                <div style={{
                    padding: "12px 16px 16px 16px",
                    display: "flex",
                    flexDirection: "column",
                    minWidth: 380,
                    alignItems: "center"
                }}>
                    <div style={{ height: 12 }} />

                    <h3 ref={headlineRef} style={{ display: "inline", color: "#CCC", fontWeight: 700, fontSize: 9, textAlign: "center" }}>
                        <Tooltip text={tooltip} />
                    </h3>

                    {loading ? (
                        <div style={{ fontSize: 8, color: "#CCC", textAlign: "center" }}>Loading...</div>
                    ) : error ? (
                        <div style={{ color: "#e63946", fontSize: 8, textAlign: "center" }}>
                            {error}
                            {handleUIEvent(null, { error, action: "calendar_render_error" }, null, "CalendarSelector", "_42_calendar_selector.js")}
                        </div>
                    ) : (
                        <>
                            {view === "month" && (
                                <div style={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    margin: "8px 0 12px 0",
                                    gap: 8,
                                    width: "100%"
                                }}>
                                    <button
                                        onClick={e => {
                                            setView("year");
                                            handleUIEvent(e, { action: "calendar_navigate_year_view" }, null, "CalendarSelector", "_42_calendar_selector.js");
                                        }}
                                        style={{
                                            background: "none",
                                            border: "none",
                                            color: "#888",
                                            fontSize: 16,
                                            cursor: "pointer",
                                            padding: 0
                                        }}
                                    >
                                        &lt;
                                    </button>

                                    <span
                                        onClick={e => {
                                            setView("year");
                                            handleUIEvent(e, { action: "calendar_month_clicked" }, null, "CalendarSelector", "_42_calendar_selector.js");
                                        }}
                                        style={{
                                            fontWeight: 700,
                                            fontSize: 14,
                                            color: "#CCC",
                                            cursor: "pointer",
                                            padding: "4px 8px",
                                            borderRadius: 4,
                                            transition: "background-color 0.2s",
                                            minWidth: 120,
                                            textAlign: "center"
                                        }}
                                        onMouseEnter={e => e.target.style.backgroundColor = "rgba(0,255,255,0.1)"}
                                        onMouseLeave={e => e.target.style.backgroundColor = "transparent"}
                                        title="Click to select month"
                                    >
                                        {getMonthFull(selectedMonth)}
                                    </span>

                                    <span
                                        onClick={e => {
                                            setView("years");
                                            handleUIEvent(e, { action: "calendar_year_clicked" }, null, "CalendarSelector", "_42_calendar_selector.js");
                                        }}
                                        style={{
                                            fontWeight: 700,
                                            fontSize: 14,
                                            color: "#CCC",
                                            cursor: "pointer",
                                            padding: "4px 8px",
                                            borderRadius: 4,
                                            transition: "background-color 0.2s"
                                        }}
                                        onMouseEnter={e => e.target.style.backgroundColor = "rgba(0,255,255,0.1)"}
                                        onMouseLeave={e => e.target.style.backgroundColor = "transparent"}
                                        title="Click to select year"
                                    >
                                        {selectedYear}
                                    </span>

                                    <button
                                        onClick={e => {
                                            setView("year");
                                            handleUIEvent(e, { action: "calendar_navigate_year_view" }, null, "CalendarSelector", "_42_calendar_selector.js");
                                        }}
                                        style={{
                                            background: "none",
                                            border: "none",
                                            color: "#888",
                                            fontSize: 16,
                                            cursor: "pointer",
                                            padding: 0
                                        }}
                                    >
                                        &gt;
                                    </button>
                                </div>
                            )}

                            {view === "month" && renderQuarterSelector()}

                            {view === "years" ? (
                                renderYearsGrid()
                            ) : view === "year" ? (
                                renderYearGrid(selectedYear)
                            ) : (
                                <div style={{ marginTop: 4, width: "100%", display: "flex", justifyContent: "center" }}>
                                    {renderCalendarGrid()}
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </>
    );
}